import React from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { BloodworkAnalysis, PanelMarker, FurtherConsiderations, Section } from '../../models';
import { AnemiaStatus } from './bloodwork-anemia-status';
import { Footer } from '../../Footer';
import { TabsComponent } from '../tabs-component';
import { WBCEvaluation } from './bloodwork-wbc-evaluation';
import { GlucoseRegulationInsights } from './glucose-regulation-insights';
import { ThyroidRegulationInsights } from './thyroid-regulation-insights';
import { StressAdrenalRegulationInsights } from './stress-adrenal-regulation-insights';
import { adrenalGauge } from '../../models/StressAdrenalAnalysis';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type Props = {
  section: Section;
  panelTitle: string;
  bloodworkAnalysis: BloodworkAnalysis;
  panelMarkers: PanelMarker[];
  furtherConsiderations: FurtherConsiderations;
  evalSmall: number;
  refSmall: number;
  evalLarge: number;
  refLarge: number;
  infoLink: string;
  bloodworkEntryStore: BloodworkEntryStore;
  enabledSupplementBrands: string[];
  collapsed: boolean;
  onToggle: () => void;
  includeInPdf: boolean;
  onIncludeInPdfToggle: () => void;
};

export class BloodworkTestAnalysisSection extends React.Component<Props, {}> {

  evaluation = () => {
    const { bloodworkAnalysis, section, evalSmall, evalLarge } = this.props;
    if (section === Section.RBC) {
      return (
        <Col xs={evalSmall} lg={evalLarge}>
          <AnemiaStatus bloodworkAnalysis={bloodworkAnalysis} />
        </Col>  
      );
    } else if (section === Section.WBC && bloodworkAnalysis.wbc.hasValue()) {
      return (
        <Col xs={evalSmall} lg={evalLarge}>
          <WBCEvaluation bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else if (section === Section.GLUCOSE) {
      // When the insights panel renders nothing (e.g. amylase is the only
      // glucose-section marker), skip the eval Col entirely so the Results
      // panel left-aligns instead of sitting behind an empty left gap.
      if (!GlucoseRegulationInsights.shouldRender(bloodworkAnalysis)) {
        return null;
      }
      return (
        <Col xs={evalSmall} lg={evalLarge}>
          <GlucoseRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else if (section === Section.THYROID && process.env.REACT_APP_ENV !== 'prod' && process.env.REACT_APP_ENV !== 'slava') {
      return (
        <Col xs={evalSmall} lg={evalLarge}>
          <ThyroidRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else if (section === Section.STRESS_ADRENAL && process.env.REACT_APP_ENV !== 'prod' && process.env.REACT_APP_ENV !== 'slava') {
      // With no Na/K ratio the insights gauge renders nothing (see
      // StressAdrenalRegulationInsights). Skip the eval Col entirely so the
      // Results panel left-aligns like sections without an insights panel,
      // instead of sitting in the right column behind an empty left gap.
      if (!adrenalGauge(bloodworkAnalysis.sodiumPotassiumRatio)) {
        return null;
      }
      return (
        <Col xs={evalSmall} lg={evalLarge}>
          <StressAdrenalRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else {
      return null;
    }
  }

  analysis = () => {
    const { section, bloodworkAnalysis, panelMarkers, furtherConsiderations,
      refSmall, refLarge, bloodworkEntryStore, infoLink, enabledSupplementBrands } = this.props;
      return (
        <Col xs={refSmall} lg={refLarge}>
          <TabsComponent
            section={section}
            panelMarkers={panelMarkers}
            bloodworkAnalysis={bloodworkAnalysis} 
            furtherConsiderations={furtherConsiderations}
            bloodworkEntryStore={bloodworkEntryStore}
            infoLink={infoLink}
            enabledSupplementBrands={enabledSupplementBrands}
          />
        </Col>
      );
  }

  render() {
    const { panelTitle, panelMarkers, collapsed, onToggle, section, includeInPdf, onIncludeInPdfToggle } = this.props;
    const chevron = collapsed ? 'chevron-down' : 'chevron-up';
    return (
      <>
      { panelMarkers.length > 0 ?
        <Card>
          <Card.Header
            onClick={onToggle}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <span>
              <FontAwesomeIcon icon={chevron} />&nbsp;&nbsp;{panelTitle}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 16 }}>
              <label
                htmlFor={`include-in-pdf-${section}`}
                onClick={e => e.stopPropagation()}
                style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                title="Include this section in the Analysis PDF"
              >
                <span style={{ marginRight: '6px' }}>Include in PDF</span>
                <input
                  id={`include-in-pdf-${section}`}
                  type="checkbox"
                  className="lsm-styled-checkbox"
                  checked={includeInPdf}
                  onChange={onIncludeInPdfToggle}
                />
              </label>
            </span>
          </Card.Header>
          {!collapsed && (
            <>
              <Card.Body>
                <Row>
                  {this.evaluation()}
                  {this.analysis()}
                </Row>
              </Card.Body>
              <Card.Footer>
                <Footer />
              </Card.Footer>
            </>
          )}
        </Card>
        : null
      }
      </>
    );
  }
}
